import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const CHUNK_RELOAD_KEY = 'shopgnd-chunk-reload';

export function isStaleChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error ?? '');
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

async function importWithRetry<T>(
  loader: () => Promise<T>,
  retries = 1,
): Promise<T> {
  try {
    return await loader();
  } catch (error) {
    if (!isStaleChunkLoadError(error) || retries <= 0) throw error;
    await new Promise((r) => setTimeout(r, 400));
    return importWithRetry(loader, retries - 1);
  }
}

/** One hard reload per session when a lazy route chunk 404s after a new deploy. */
function reloadForStaleChunks(): never {
  const tried = sessionStorage.getItem(CHUNK_RELOAD_KEY);
  if (!tried) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
    window.location.reload();
  }
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
  throw new Error('Failed to load application bundle. Please refresh the page.');
}

export function clearChunkReloadFlag(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
}

export function lazyDefaultWithRetry(
  factory: () => Promise<{ default: ComponentType<object> }>,
): LazyExoticComponent<ComponentType<object>> {
  return lazy(() =>
    importWithRetry(factory).catch((error) => {
      if (isStaleChunkLoadError(error)) reloadForStaleChunks();
      throw error;
    }),
  );
}

export function lazyNamedWithRetry(
  factory: () => Promise<Record<string, unknown>>,
  name: string,
): LazyExoticComponent<ComponentType<object>> {
  return lazy(() =>
    importWithRetry(() =>
      factory().then((m) => {
        const component = m[name] as ComponentType<object> | undefined;
        if (!component) {
          throw new Error(`Lazy module missing export: ${name}`);
        }
        return { default: component };
      }),
    ).catch((error) => {
      if (isStaleChunkLoadError(error)) reloadForStaleChunks();
      throw error;
    }),
  );
}
